import type {CompRef, Pointer, VariablePointer} from '@wix/document-services-types'
import {pointerUtils, type DAL, type ExtensionAPI} from '@wix/document-manager-core'
import {ReportableError} from '@wix/document-manager-utils'
import * as constants from '../../constants/constants'
import type {VariablesExtensionAPI} from './variables'

const {getPointer} = pointerUtils

const VARIABLES_NAMESPACE = constants.DATA_TYPES.variables

const createVariablesError = (message: string) => {
    return new ReportableError({
        message,
        errorType: 'variablesError'
    })
}

export const collectVariableConnections = (
    dal: DAL,
    variableConnections: object
): {propertyPath: string; variablePointer: VariablePointer}[] => {
    const variablePointersToResolve: {propertyPath: string; variablePointer: VariablePointer}[] = []

    for (const [propertyPath, variableId] of Object.entries(variableConnections)) {
        if (typeof variableId === 'string') {
            const referencedVariableId = variableId.replace('#', '')
            const referencedVariablePointer = getPointer(referencedVariableId, VARIABLES_NAMESPACE) as VariablePointer

            if (!dal.has(referencedVariablePointer)) {
                throw createVariablesError('Referenced variable does not exist')
            }

            variablePointersToResolve.push({
                propertyPath,
                variablePointer: referencedVariablePointer
            })
        }
    }

    return variablePointersToResolve
}

export const createVariableDependencyTree = (
    dal: DAL,
    extensionAPI: ExtensionAPI,
    variablePointers: VariablePointer[],
    compPointer: CompRef
): Record<string, {variable: Pointer; dependencies: string[]; dependents: string[]}> => {
    const {variables} = extensionAPI as VariablesExtensionAPI
    const dependencyTree: Record<string, {variable: Pointer; dependencies: string[]; dependents: string[]}> = {}

    // Initialize the tree with all variables
    for (const variablePointer of variablePointers) {
        dependencyTree[variablePointer.id] = {
            variable: variablePointer,
            dependencies: [],
            dependents: []
        }
    }

    // Build dependency relationships
    for (const variablePointer of variablePointers) {
        const variableData = variables.getVariableData(compPointer, variablePointer, false)
        if (!variableData) continue

        // Check if this variable has variableConnections
        if (variableData.variableConnections && typeof variableData.variableConnections === 'object') {
            const variableConnections = collectVariableConnections(dal, variableData.variableConnections)

            for (const {variablePointer: dependentVariablePointer} of variableConnections) {
                const dependentId = dependentVariablePointer.id

                // Add dependency relationship
                if (dependencyTree[variablePointer.id] && dependencyTree[dependentId]) {
                    // variablePointer has connections to dependentVariablePointer
                    // So variablePointer is the parent, dependentVariablePointer is the child
                    dependencyTree[variablePointer.id].dependents.push(dependentId)
                    dependencyTree[dependentId].dependencies.push(variablePointer.id)
                }
            }
        }
    }

    return dependencyTree
}
